import React, { useCallback, useEffect, useState } from 'react'
import type { Patient } from '@dataTypes/PatientInfo.types'
import type { PendingFormEntry } from '@pages/FormBuilder/assignedForms.types'
import { getPendingFormEntries } from '@services/api/forms.api'
import { getFormsSocket } from '@services/websocket/socket'
import Button from '@components/common/Button/Button.component'
import PendingFormsTable from '@components/pages/FormBuilder/PendingFormsTable'
import CompletedFormsTable from '@components/pages/FormBuilder/CompletedFormsTable'
import SendPatientFormsModal from '@components/pages/FormBuilder/SendingFormsTable/SendPatientFormsModal.component'
import PatientFormsView from '@pages/FormBuilder/PatientFormsView.page'
import CompletedFormsView, {
  CompletedFormsViewLocationState,
} from '@pages/FormBuilder/CompletedFormsView.page'
import {
  countFormRequests,
  filterFormRequestSources,
} from '@pages/FormBuilder/formRequests.util'

const PatientProfileForms: React.FC<{ patient: Patient }> = ({ patient }) => {
  const [entries, setEntries] = useState<PendingFormEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [tab, setTab] = useState<'pending' | 'completed'>('pending')
  const [source, setSource] = useState('All')
  const [showSend, setShowSend] = useState(false)
  const [viewForms, setViewForms] = useState(false)
  const [detail, setDetail] = useState<CompletedFormsViewLocationState | null>(
    null,
  )
  const [refreshKey, setRefreshKey] = useState(0)
  const refresh = useCallback(() => setRefreshKey((key) => key + 1), [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(false)
    getPendingFormEntries({ patient_id: patient.patient_id })
      .then((rows) => {
        if (!cancelled) setEntries(rows)
      })
      .catch(() => {
        if (!cancelled) setError(true)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [patient.patient_id, refreshKey])

  useEffect(() => {
    const socket = getFormsSocket()
    if (!socket) return
    socket.on('forms.assignment.completed', refresh)
    return () => {
      socket.off('forms.assignment.completed', refresh)
    }
  }, [refresh])

  const scoped = filterFormRequestSources(
    entries,
    source === 'All' ? [] : [source],
  )
  const pendingCount = scoped.reduce(
    (count, entry) => count + countFormRequests(entry, 'pending'),
    0,
  )
  const completedCount = scoped.reduce(
    (count, entry) => count + countFormRequests(entry, 'completed'),
    0,
  )
  const onBack = (): void => {
    setViewForms(false)
    setDetail(null)
    refresh()
  }

  return (
    <div className="patient-profile-card">
      <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
        <h2>Forms</h2>
        <Button className="button-primary" onClick={() => setShowSend(true)}>
          Send Form
        </Button>
      </div>
      {viewForms ? (
        <PatientFormsView
          patientId={patient.patient_id}
          patientName={`${patient.first_name} ${patient.last_name}`}
          onBackToDashboard={onBack}
        />
      ) : detail ? (
        <CompletedFormsView payload={detail} onBackToDashboard={onBack} />
      ) : (
        <>
          <div className="d-flex align-items-center gap-2 mb-3">
            <Button
              className={
                tab === 'pending' ? 'button-primary' : 'button-secondary'
              }
              onClick={() => setTab('pending')}
            >
              Pending forms ({pendingCount})
            </Button>
            <Button
              className={
                tab === 'completed' ? 'button-primary' : 'button-secondary'
              }
              onClick={() => setTab('completed')}
            >
              Completed forms ({completedCount})
            </Button>
            <label htmlFor="patient-form-source">Request source</label>
            <select
              id="patient-form-source"
              className="form-select w-auto"
              value={source}
              onChange={(event) => setSource(event.target.value)}
            >
              <option>All</option>
              <option>Appointment</option>
              <option>Direct</option>
            </select>
          </div>
          {loading ? (
            <p>Loading forms…</p>
          ) : error ? (
            <div role="alert">
              Failed to load forms.{' '}
              <Button className="button-secondary" onClick={refresh}>
                Retry
              </Button>
            </div>
          ) : tab === 'pending' ? (
            pendingCount ? (
              <PendingFormsTable
                data={scoped.filter(
                  (entry) => countFormRequests(entry, 'pending') > 0,
                )}
                onOpenPatientForms={() => setViewForms(true)}
                onOpenCompletedDetail={setDetail}
              />
            ) : (
              <p>No pending forms.</p>
            )
          ) : completedCount ? (
            <CompletedFormsTable
              data={scoped
                .filter((entry) => countFormRequests(entry, 'completed') > 0)
                .map((entry) => ({
                  patientId: entry.patientId,
                  patientName: entry.patientName,
                  publicPatientId: entry.publicPatientId,
                  patientMobile: patient.cellphone,
                  completedFormsCount: countFormRequests(entry, 'completed'),
                }))}
              onOpenPatientForms={() => setViewForms(true)}
            />
          ) : (
            <p>No completed forms.</p>
          )}
        </>
      )}
      <SendPatientFormsModal
        show={showSend}
        onHide={() => setShowSend(false)}
        patient={patient}
        onFormsAssigned={refresh}
      />
    </div>
  )
}

export default PatientProfileForms
